import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

interface AdminShortcut {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  description: string;
  action: () => void;
}

export function useAdminKeyboardShortcuts() {
  const navigate = useNavigate();

  const shortcuts: AdminShortcut[] = [
    // Navigation shortcuts (G + key)
    { key: "d", description: "Go to Dashboard", action: () => navigate("/admins/dashboard") },
    { key: "u", description: "Go to Users", action: () => navigate("/admins/users") },
    { key: "r", description: "Go to Revenue", action: () => navigate("/admins/revenue") },
    { key: "s", description: "Go to Security", action: () => navigate("/admins/security") },
    { key: "h", description: "Go to Health", action: () => navigate("/admins/health") },
    { key: "f", description: "Go to Feature Flags", action: () => navigate("/admins/feature-flags") },
    // Back to app
    { key: "b", description: "Back to App", action: () => navigate("/dashboard") },
  ];

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Ignore if typing in an input
    if (
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement ||
      event.target instanceof HTMLSelectElement
    ) {
      return;
    }

    // G + key navigation
    if (event.key === "g" || event.key === "G") {
      const handleNextKey = (nextEvent: KeyboardEvent) => {
        const shortcut = shortcuts.find(s => s.key === nextEvent.key.toLowerCase());
        if (shortcut) {
          nextEvent.preventDefault();
          shortcut.action();
        }
        document.removeEventListener("keydown", handleNextKey);
      };
      
      // Wait for next key
      document.addEventListener("keydown", handleNextKey, { once: true });
      
      // Timeout to cancel if no key pressed
      setTimeout(() => {
        document.removeEventListener("keydown", handleNextKey);
      }, 1000);
    }

    // ? for help (show shortcuts)
    if (event.key === "?") {
      // Could trigger a help modal here
      console.log("Keyboard shortcuts:", shortcuts.map(s => `G + ${s.key}: ${s.description}`));
    }
  }, [navigate, shortcuts]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return { shortcuts };
}
